import { ROOT_URL } from "fleet/constant/media-library";
import moment from "moment";
import PropTypes from "prop-types";
import React from "react";
import {
  Button,
  FormCheck,
  Col,
  FormLabel,
  FormGroup,
  FormText,
  Modal,
  Row,
  Card,
} from "react-bootstrap";
import Datetime from "react-datetime";
import { Link } from "react-router-dom";
import Select from "react-select";
import { BackButton } from "ui/button/back";
import { Notification } from "ui/notification";
import { getErrorMessage } from "util/response";
import { Box } from "view/layout/box";
import { Page } from "view/layout/page";

class DeployV2Page extends React.Component {
  static get displayName() {
    return "DeployV2Page";
  }

  static get propTypes() {
    return {
      scheduleDeployment: PropTypes.func.isRequired,
      strategies: PropTypes.array,
      targets: PropTypes.array,
    };
  }

  static get defaultProps() {
    return {
      strategies: [],
      targets: [],
    };
  }

  constructor(props) {
    super(props);

    this.state = {
      deployIndefinitely: true,
      deployNow: true,
      endDate: null,
      errorMessage: "",
      showModal: false,
      successMessage: "",
      successLink: "",
      submitted: false,
      selectedStrategy: null,
      selectedTargetDevices: null,
      startDate: null,
    };

    this._closeModal = this._closeModal.bind(this);
    this._deploy = this._deploy.bind(this);
    this._onDateChange = this._onDateChange.bind(this);
    this._onSelectStrategy = this._onSelectStrategy.bind(this);
    this._onSelectTargetDevices = this._onSelectTargetDevices.bind(this);
    this._onTimeChange = this._onTimeChange.bind(this);
    this._onToggleDeployIndefinitely =
      this._onToggleDeployIndefinitely.bind(this);
    this._onToggleDeployNow = this._onToggleDeployNow.bind(this);
    this._openModal = this._openModal.bind(this);
    this._submit = this._submit.bind(this);
  }

  _openModal() {
    this.setState({ showModal: true });
  }

  _closeModal() {
    this.setState({ showModal: false });
  }

  _getDefaultStartDate() {
    // We set the default date to 12 hours ago as will be sent as UTC time
    // and interpreted on scheduled deployment as the player's local time
    return moment().subtract(12, "hours");
  }

  _getStartDate() {
    const { deployNow, startDate } = this.state;
    return deployNow ? null : startDate;
  }

  _getEndDate() {
    const { deployIndefinitely, endDate } = this.state;
    return deployIndefinitely ? null : endDate;
  }

  _formatDate(date) {
    return date ? date.format("YYYY-MM-DD HH:mm:00") : null;
  }

  _isFutureDay(date) {
    // You can select up to 1 day before to account for time zone differences.
    return moment().subtract(1, "days").startOf("day").isSameOrBefore(date);
  }

  _onSelectStrategy(value) {
    this.setState({ selectedStrategy: value });
  }

  _onSelectTargetDevices(value) {
    this.setState({ selectedTargetDevices: value });
  }

  _onToggleDeployNow(e) {
    const target = e.currentTarget;
    const deployNow = target.type === "checkbox" ? target.checked : false;

    const newState = { deployNow };
    if (deployNow) {
      newState.startDate = null;
    }
    this.setState(newState);
  }

  _onToggleDeployIndefinitely(e) {
    const target = e.currentTarget;
    const deployIndefinitely =
      target.type === "checkbox" ? target.checked : false;

    const newState = { deployIndefinitely };
    if (deployIndefinitely) {
      newState.endDate = null;
    }
    this.setState(newState);
  }

  _onDateChange(date, field) {
    if (!moment.isMoment(date)) {
      return;
    }

    // Reset time to 12AM
    date = date.clone().startOf("day");
    this.setState({ [field]: date });
  }

  _onTimeChange(date, field) {
    if (!moment.isMoment(date)) {
      return;
    }

    // If time is selected before date, default to yesterday to account for
    // timezone differences.
    const curDate = this.state[field] || moment().subtract(1, "days");

    const newDate = curDate.clone().set({
      hour: date.hour(),
      minute: date.minutes(),
    });
    this.setState({ [field]: newDate });
  }

  _getDateErrorMessage(deployNow, deployIndefinitely, startDate, endDate) {
    if (!startDate && !deployNow) {
      return "Please set a start date or check deploy now.";
    }

    if (!endDate && !deployIndefinitely) {
      return "Please set an end date or check keep running indefinitely.";
    }

    return null;
  }

  _submit() {
    if (this.state.deployNow) {
      this._openModal();
      return;
    }

    this._deploy();
  }

  _deploy() {
    const startDate = this._getStartDate();
    const endDate = this._getEndDate();
    const {
      deployIndefinitely,
      deployNow,
      selectedStrategy: strategy,
      selectedTargetDevices: targets,
    } = this.state;
    const targetIds = [];

    let errMessage = this._getDateErrorMessage(
      deployNow,
      deployIndefinitely,
      startDate,
      endDate,
    );

    if (!targets || !targets.length) {
      errMessage = "Please select targets for your deployment";
    }

    if (!strategy || !strategy.value) {
      errMessage = "Please select a strategy for your deployment";
    }

    if (errMessage) {
      this.setState({
        errorMessage: `Deployment failed. ${errMessage}`,
        showModal: false,
        successMessage: "",
        successLink: "",
      });
      return;
    }

    for (const target of targets) {
      targetIds.push(target.value);
    }

    const deployDate = this._formatDate(
      startDate || this._getDefaultStartDate(),
    );

    this.setState({ submitted: true }, () =>
      this._doDeploy({
        deployDate,
        deployNow,
        targetIds,
        endDate: this._formatDate(endDate),
        strategyId: strategy.value,
      }),
    );
  }

  _doDeploy(args) {
    return this.props
      .scheduleDeployment(args)
      .then(() => {
        const successMessage =
          "Strategy successfully scheduled to deploy on" +
          " the target devices.";
        const successLink = ROOT_URL + "/scheduled";
        this.setState({
          errorMessage: "",
          csvData: null,
          successMessage,
          successLink,
        });
      })
      .catch((e) => {
        this.setState({
          errorMessage: getErrorMessage(e),
          successMessage: "",
          successLink: "",
        });
      })
      .finally(() => {
        this.setState({
          showModal: false,
          submitted: false,
        });
      });
  }

  _renderDeploymentNotes() {
    return (
      <Card size="sm" className="well well-sm">
        <strong>Please Note: </strong>
        <ul>
          <li>
            Deployment dates are converted to the local time for the device(s)
          </li>
          <li>
            Scheduling a deployment <em>without</em> an end date does not
            guarantee the deployment will run immediately.
            <br />
            <em>
              <strong>
                Deployments with end dates have higher priority than those
                without.
              </strong>
            </em>
          </li>
        </ul>
      </Card>
    );
  }

  render() {
    const {
      deployIndefinitely,
      deployNow,
      errorMessage,
      selectedStrategy,
      selectedTargetDevices,
      showModal,
      submitted,
      successMessage,
      successLink,
    } = this.state;

    return (
      <Page title="Deploy Strategy">
        <Modal className="dialog-modal" show={showModal}>
          <Modal.Body> {this._renderDeploymentNotes()}</Modal.Body>
          <Modal.Footer>
            <Button
              variant="success"
              disabled={submitted}
              id="confirm-deploy-btn"
              onClick={this._deploy}
            >
              OK
            </Button>
            <Button onClick={this._closeModal} variant="secondary">Cancel</Button>
          </Modal.Footer>
        </Modal>
        <Box noHeader={true}>
          <Row>
            <Col xs={8}>
              <Notification
                error={errorMessage}
                success={successMessage}
                successChildren={
                  <ul>
                    <li>
                      <Link to={successLink}>View Deployments</Link>
                    </li>
                  </ul>
                }
              />
              {this._renderDeploymentNotes()}
              <Row>
                <Col xs={8}>
                  <FormGroup controlId="strategy">
                    <FormLabel>Strategy</FormLabel>
                    <Select
                      name="strategy-select"
                      value={selectedStrategy}
                      onChange={this._onSelectStrategy}
                      options={this._getStrategyOptions()}
                      required
                    />
                    <FormText>
                      Required. Please select the target strategy you want to
                      run on your devices.
                    </FormText>
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col xs={8}>
                  <FormGroup>
                    <FormLabel>Target Devices</FormLabel>
                    <Select
                      name="target-select"
                      onChange={this._onSelectTargetDevices}
                      options={this.props.targets}
                      value={selectedTargetDevices}
                      required
                      isMulti
                    />
                    <FormText>
                      Required. Please select the target devices you want the
                      above strategy to run on. You can select individual
                      devices as well as tags.
                    </FormText>
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <FormGroup>
                    <FormLabel>Start Date</FormLabel>
                    <FormCheck
                      className="deploy-date-checkbox ml-2"
                      checked={deployNow}
                      inline
                      label="Deploy Now"
                      onChange={this._onToggleDeployNow}
                      type="checkbox"
                    />
                    <strong> or </strong>
                    <Datetime
                      value={this._getStartDate()}
                      className="deploy-datepicker"
                      inputProps={{
                        onClick: this._onToggleDeployNow,
                        placeholder: "Select start date.",
                        readOnly: deployNow,
                      }}
                      isValidDate={this._isFutureDay}
                      timeFormat={false}
                      onChange={(d) => this._onDateChange(d, "startDate")}
                    />
                    <Datetime
                      value={this._getStartDate()}
                      dateFormat={false}
                      className="deploy-timepicker"
                      inputProps={{
                        onClick: this._onToggleDeployNow,
                        placeholder: "Select start time.",
                        readOnly: deployNow,
                      }}
                      onChange={(t) => this._onTimeChange(t, "startDate")}
                    />
                    <FormText>
                      Please select the date and time you would you like your
                      strategy to start running.
                    </FormText>
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <FormGroup>
                    <FormLabel>End Date</FormLabel>
                    <FormCheck
                      className="deploy-date-checkbox ml-2"
                      checked={deployIndefinitely}
                      inline
                      label="Keep Running Indefinitely"
                      onChange={this._onToggleDeployIndefinitely}
                      type="checkbox"
                    />
                    <strong> or </strong>
                    <Datetime
                      value={this._getEndDate()}
                      className="deploy-datepicker"
                      inputProps={{
                        onClick: this._onToggleDeployIndefinitely,
                        readOnly: deployIndefinitely,
                        placeholder: "Select end date.",
                      }}
                      isValidDate={this._isFutureDay}
                      timeFormat={false}
                      onChange={(d) => {
                        this._onDateChange(d, "endDate");
                      }}
                      ref={(r) => {
                        this.endDatePicker = r;
                      }}
                    />
                    <Datetime
                      value={this._getEndDate()}
                      className="deploy-timepicker"
                      dateFormat={false}
                      inputProps={{
                        onClick: this._onToggleDeployIndefinitely,
                        readOnly: deployIndefinitely,
                        placeholder: "Select end time.",
                      }}
                      onChange={(t) => this._onTimeChange(t, "endDate")}
                      ref={(r) => {
                        this.endTimePicker = r;
                      }}
                    />
                    <FormText>
                      Please select the date and time you would you like your
                      strategy to finish running or check the box to keep your
                      strategy running indefinitely.
                    </FormText>
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <BackButton />
                  <Button
                    variant="primary"
                    disabled={submitted}
                    id="deploy-btn"
                    onClick={this._submit}
                  >
                    Deploy
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </Box>
      </Page>
    );
  }

  _getStrategyOptions() {
    return this.props.strategies.map((strategy) => {
      return {
        label: strategy.name,
        value: strategy.id,
      };
    });
  }
}

export { DeployV2Page };
